import type { Accommodation } from "@@/shared/acquire/accommodation";
import { isOrder, orderHasAccommodation } from "@@/shared/orders/is-order";
import { isOrderItem } from "@@/shared/orders/order-items/is-order-item";
import { isOrderItemBookable_V1 } from "@@/shared/orders/order-items/is-order-item-v1";
import type { OrderItemAccommodation_V2 } from "@@/shared/orders/order-items/order-item-v2";
import type { Content } from "@@/shared/pages/content/content";
import type { CustomContent } from "@@/shared/pages/content/custom-content/custom-content";
import type { Page } from "@@/shared/pages/page";
import type { Order } from "../orders/order";
import type { OrderItem } from "../orders/order-items/order-item";
import type { OrderItem_V1 } from "../orders/order-items/order-item-v1";
import { isProduct } from "../products/is-product";
import type { Accommodation_Product, Product } from "../products/product";
import type { AccommodationSku, Sku } from "../products/sku";
import { isAccommodationSKu } from "../products/sku";
import { isAcquirable, type Acquirable } from "./acquirable";

function isAccommodation(acquirable: Acquirable): acquirable is Accommodation;
function isAccommodation(order: Order | undefined): boolean;
function isAccommodation(
    product: Product | undefined,
): product is Accommodation_Product;
function isAccommodation(sku: Sku | undefined): sku is AccommodationSku;
function isAccommodation(page: Page | undefined): boolean;
function isAccommodation(content: Content | undefined): boolean;
function isAccommodation(customContent: CustomContent | undefined): boolean;
function isAccommodation(
    orderItem: OrderItem,
): orderItem is OrderItemAccommodation_V2;
function isAccommodation(
    orderItem: OrderItem | undefined,
): orderItem is OrderItemAccommodation_V2;
function isAccommodation(
    value:
        | Acquirable
        | Product
        | Sku
        | Order
        | OrderItem_V1
        | OrderItem
        | Page
        | CustomContent
        | undefined,
): boolean {
    if (typeof value === "undefined") return false;
    if (value._type === "PAGE") return false;
    if (value._type === "CUSTOM_CONTENT") return false;
    if (isAccommodationSKu(value)) return true;
    if (isAcquirable(value) && value._type === "ACCOMMODATION") return true;
    if (isProduct(value) && value.acquirableAs === "ACCOMMODATION") return true;
    if (isOrder(value) && orderHasAccommodation(value)) return true;
    if (isOrderItemBookable_V1(value)) return true;
    if (isOrderItem(value) && value.acquireType === "ACCOMMODATION")
        return true;

    return false;
}

export { isAccommodation };
